import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AppState, AppTheme, ProfileReducerState, GalleryInterface, GalleryReducerState, GalleriesReducerState } from "../../../interfaces";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Grid,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { DialogModal, ErrorView } from "../../../components";
import { clearGalleryError, getGalleries, deleteGallery } from "../../../store/actions";
import { checkUserAccess } from "../../../util/permission-checker";
import PopupMenuDialog from "../../../components/PopupMenuDialog";
import UpsertGalleryDetailsModal from "./Modals/UpsertGalleryDetailsDialog";
import GalleryPhotoDialog from "./Modals/GalleryPhotoDialog";

interface GalleriesProps {
  galleries: GalleriesReducerState;
  gallery: GalleryReducerState;
  profile: ProfileReducerState;
  appTheme: AppTheme;
  getGalleries: (search: string, page: number, per_page: number) => void;
  clearGalleryError: () => void;
  deleteGallery: (_id: string) => void;
}

const useStyles = (appTheme: AppTheme) =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
        width: "100%"
      },
      paper: {
        backgroundColor: appTheme.section.backgroundColor,
        width: "100%",
        padding: 30,
        marginTop: 20,
        marginBottom: 20
      },
      icon: {
        color: theme.palette.text.primary,
        padding: 10,
        borderRadius: 30,
        backgroundColor: appTheme.iconBackgroundColor,
        "&:hover": {
          backgroundColor: appTheme.iconBackgroundHoverColor
        }
      },
      searchButton: {
        marginRight: "-21px",
        height: "55px",
        borderRadius: "30px",
        width: "100px"
      },
      textInput: {
        marginTop: "5px",
        width: "300px"
      },
      textInputControl: {
        borderRadius: "30px",
        backgroundColor: appTheme.section.backgroundColor
      },
      container: {
        minHeight: "60vh"
      },
      textInputIcon: {
        color: "#000"
      }
    })
  );

const Galleries: React.FC<GalleriesProps> = (props) => {
  const { appTheme, galleries, gallery, profile, getGalleries, deleteGallery, clearGalleryError } = props;

  const [search, setSearch] = useState<string>(galleries.search);
  const [page, setPage] = useState<number>(galleries.page);
  const [perPage, setPerPage] = useState<number>(galleries.per_page ?? 20);
  const [selectedRecord, setSelectedRecord] = useState<GalleryInterface | null>(null);
  const [popUpDialogOpen, setPopUpDialogOpen] = useState<boolean>(false);

  const [upsertGalleryDetailsModelOpen, setUpsertGalleryDetailsModelOpen] = useState<boolean>(false);
  const [galleryPhotoModelOpen, setGalleryPhotoModelOpen] = useState<boolean>(false);

  const popUpMenuItems = useMemo(() => {
    return [
      { text: "View Gallery Photos", permission: "view-galleries" },
      { text: "Update Gallery Details", permission: "edit-gallery" },
      { text: "Delete Gallery", permission: "delete-gallery" }
    ];
  }, []);

  const [menuItems, setMenuItems] = useState<Array<string>>(
    popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text)
  );

  useEffect(() => {
    setMenuItems(popUpMenuItems.filter((menuItem) => checkUserAccess(menuItem.permission, profile.permissions)).map((menuItem) => menuItem.text));
  }, [popUpMenuItems, profile.permissions]);

  useEffect(() => {
    getGalleries(search, page, perPage);
  }, [search, page, perPage, getGalleries]);

  const styles = useStyles(appTheme)();

  type DialogOptions = {
    open: boolean;
    body: string;
  };

  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({ open: false, body: "" });

  const dismissDialogModal = useCallback(() => {
    setDialogOptions({ open: false, body: "" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      clearGalleryError();
    };
  }, [clearGalleryError]);

  useEffect(() => {
    if (galleryPhotoModelOpen) {
      return;
    }

    if (!gallery.error && gallery.response) {
      if (gallery.response.data.status === "Ok") {
        clearGalleryError();
        return;
      }
      clearGalleryError();

      setSelectedRecord(null);

      setUpsertGalleryDetailsModelOpen(false);

      setGalleryPhotoModelOpen(false);

      setDialogOptions({ open: true, body: gallery.response.data.status });

      getGalleries("", 0, perPage);
    }
  }, [galleryPhotoModelOpen, gallery.error, gallery.response, perPage, getGalleries, clearGalleryError]);

  useEffect(() => {
    if (upsertGalleryDetailsModelOpen || galleryPhotoModelOpen) {
      return;
    }

    const requestData = gallery.error ? gallery : null;

    if (!requestData) {
      return;
    }

    if (requestData.error && requestData.error.data && requestData.error.status) {
      let body: string = requestData.error.data.status;

      if (requestData.error.status === 500) {
        body = requestData.error.data.content;
      }

      setDialogOptions({ open: true, body: body });
    } else {
      if (requestData.error && requestData.error.message) {
        setDialogOptions({ open: true, body: requestData.error.message });
      }
    }
  }, [gallery, galleryPhotoModelOpen, upsertGalleryDetailsModelOpen]);

  const onMenuItemSelected = (menuItem: string) => {
    setPopUpDialogOpen(false);

    if (!selectedRecord) {
      return;
    }

    switch (menuItem) {
      case "View Gallery Photos":
        setGalleryPhotoModelOpen(true);
        break;
      case "Update Gallery Details":
        setUpsertGalleryDetailsModelOpen(true);
        break;
      case "Delete Gallery":
        if (window.confirm("Would you like to delete this gallery ?")) {
          deleteGallery(selectedRecord.gallery_id);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.root}>
      <DialogModal open={dialogOptions.open} body={dialogOptions.body} onDismissed={dismissDialogModal} />
      <UpsertGalleryDetailsModal
        open={upsertGalleryDetailsModelOpen}
        onDismissed={() => setUpsertGalleryDetailsModelOpen(false)}
        galleryData={selectedRecord}
      />
      <GalleryPhotoDialog
        open={galleryPhotoModelOpen}
        onDismissed={() => {
          setGalleryPhotoModelOpen(false);
          getGalleries(search, page, perPage);
        }}
        galleryData={selectedRecord}
      />
      <PopupMenuDialog
        open={popUpDialogOpen}
        onDismissed={() => setPopUpDialogOpen(false)}
        onMenuItemSelected={onMenuItemSelected}
        menuItems={menuItems}
      />
      <Container maxWidth="lg">
        {galleries.error ? (
          <ErrorView error={galleries.error} onRetry={() => getGalleries(search, page, perPage)} />
        ) : (
          <React.Fragment>
            <Paper className={styles.paper}>
              <Grid container justifyContent="space-between" spacing={0}>
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      {(galleries.loading || gallery.loading) && <CircularProgress style={{ display: "block", margin: "auto" }} color="primary" />}
                    </Grid>
                    <Grid item>
                      <Typography variant="h3" color="primary">
                        Galleries
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(event) => {
                      setSearch(event.currentTarget.value);
                    }}
                    error={false}
                    value={search}
                    variant="outlined"
                    placeholder="Search ..."
                    className={styles.textInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className={styles.textInputIcon} />
                        </InputAdornment>
                      ),
                      classes: { root: styles.textInputControl }
                    }}
                  />
                </Grid>
              </Grid>
              <br />
              <TableContainer className={styles.container}>
                <Table stickyHeader aria-label="galleries">
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "ID", minWidth: 100 },
                        { label: "Title", minWidth: 250 },
                        { label: "Description", minWidth: 350 },
                        { label: "Photos", minWidth: 100 },
                        { label: "Options", minWidth: 100 }
                      ].map((column) => (
                        <TableCell key={column.label} align="center" style={{ minWidth: column.minWidth }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {galleries.data.map((gallery: GalleryInterface) => {
                      const _id = gallery.gallery_id;
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={_id}>
                          {["gallery_id", "title", "description", "photos", "options"].map((column) => {
                            const key = `${_id}-${column}`;

                            const data = (gallery as unknown as { [key: string]: any })[column];
                            if (column === "options") {
                              if (menuItems.length === 0) return <TableCell key={key} align="center"></TableCell>;
                              return (
                                <TableCell key={key} align="center">
                                  <Button
                                    disabled={menuItems.length === 0}
                                    variant="outlined"
                                    onClick={() => {
                                      setSelectedRecord(gallery);
                                      setPopUpDialogOpen(true);
                                    }}>
                                    Options
                                  </Button>
                                </TableCell>
                              );
                            } else if (column === "photos") {
                              return (
                                <TableCell key={key} align="center">
                                  {data.length}
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell key={key} align="center">
                                  {data}
                                </TableCell>
                              );
                            }
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justifyContent="space-between" style={{ margin: "0px 10px 0px 10px" }}>
                <Grid item>
                  <Button
                    disabled={!checkUserAccess("add-gallery", profile.permissions)}
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      setSelectedRecord(null);
                      setUpsertGalleryDetailsModelOpen(true);
                    }}>
                    Add New Gallery
                  </Button>
                </Grid>
                <Grid item>
                  <TablePagination
                    component="div"
                    count={galleries.total}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[20, 50, 100, 1000]}
                    onRowsPerPageChange={(event) => {
                      setPerPage(parseInt(event.target.value));
                      setPage(0);
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </React.Fragment>
        )}
      </Container>
    </div>
  );
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, void, AnyAction>) => {
  return {
    getGalleries: (search: string, page: number, per_page: number) => dispatch(getGalleries(search, page, per_page)),
    clearGalleryError: () => dispatch(clearGalleryError()),
    deleteGallery: (_id: string) => dispatch(deleteGallery(_id))
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    galleries: state.galleries,
    gallery: state.gallery,
    profile: state.profile,
    appTheme: state.general.theme
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Galleries);
